import React, { useRef, useState } from 'react';
import { Button } from './Button';
import { useToast } from '../../hooks/useToast';
import { usePresignOrderImageUploadMutation, useCreateOrderImageMutation, useDeleteOrderImageMutation, apiSlice } from '../../store/slices/apiSlice';
import { useDispatch } from 'react-redux';
import { ImageLightbox } from './ImageLightbox';
import type { FileUploadCardProps } from '../../types';

const ACCEPT = '.jpg,.jpeg,.png,.webp,.pdf,.heic,.heif';
const MAX = 5_000_000;

export const FileUploadCard: React.FC<FileUploadCardProps> = ({ orderId, feature, imageType, existingImage, onUploaded, onDeleted, onFileSelected }) => {
  const toast = useToast();
  const dispatch = useDispatch<any>();
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [image, setImage] = useState<{ id: string; content_type: string } | null>(existingImage ?? null);
  const [showLightbox, setShowLightbox] = useState(false);
  const [viewUrl, setViewUrl] = useState<string | null>(null);

  const [presign] = usePresignOrderImageUploadMutation();
  const [createImage] = useCreateOrderImageMutation();
  const [deleteImage] = useDeleteOrderImageMutation();

  const fileInput = useRef<HTMLInputElement | null>(null);

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] || null;
    if (!f) { setFile(null); setPreviewUrl(null); return; }
    if (f.size > MAX) { toast.error('File too large', 'Max 5 MB'); e.currentTarget.value = ''; return; }
    setFile(f);
    if (f.type.startsWith('image/')) {
      setPreviewUrl(URL.createObjectURL(f));
    } else {
      setPreviewUrl(null);
    }
    // If no orderId yet, notify parent that file is selected
    if (!orderId && f) {
      onFileSelected?.(f);
    }
  };

  const doUpload = async () => {
    if (!file) return;
    
    // If no orderId, just store the file for later
    if (!orderId) {
      onFileSelected?.(file);
      toast.info('Image selected', 'Image will be uploaded when you save the order.');
      return;
    }
    
    try {
      setUploading(true);
      // 1) Presign
      const pres = await presign({ orderId, content_type: file.type || 'application/octet-stream', file_size: file.size, image_type: imageType, feature }).unwrap();
      const form = new FormData();
      Object.entries(pres.fields).forEach(([k, v]) => form.append(k, v as string));
      form.append('file', file);
      // 2) Upload to S3
      const up = await fetch(pres.url, { method: 'POST', body: form });
      if (!up.ok) throw new Error('S3 upload failed');
      // 3) Create DB row
      const created = await createImage({ orderId, body: { s3_key: pres.key, content_type: file.type || 'application/octet-stream', file_size: file.size, image_type: imageType, feature } }).unwrap();
      setImage({ id: created.id, content_type: created.content_type });
      setFile(null); setPreviewUrl(null);
      toast.success('Image uploaded', 'Image uploaded successfully');
      onUploaded?.(created);
    } catch (e: any) {
      toast.error('Upload failed', e?.data?.detail || e?.message || 'Please try again');
    } finally {
      setUploading(false);
    }
  };

  const doReplace = async () => {
    if (!image || !orderId || !file) return;
    try {
      setUploading(true);
      // 1) Presign
      const pres = await presign({ orderId, content_type: file.type || 'application/octet-stream', file_size: file.size, image_type: imageType, feature }).unwrap();
      const form = new FormData();
      Object.entries(pres.fields).forEach(([k, v]) => form.append(k, v as string));
      form.append('file', file);
      // 2) Upload to S3
      const up = await fetch(pres.url, { method: 'POST', body: form });
      if (!up.ok) throw new Error('S3 upload failed');
      // 3) Create DB row
      const created = await createImage({ orderId, body: { s3_key: pres.key, content_type: file.type || 'application/octet-stream', file_size: file.size, image_type: imageType, feature } }).unwrap();
      // 4) Soft delete previous image
      try { await deleteImage({ imageId: image.id, orderId }).unwrap(); onDeleted?.(image.id); } catch {}
      setImage({ id: created.id, content_type: created.content_type });
      setFile(null); setPreviewUrl(null);
      toast.success('Image replaced', 'Previous image has been replaced.');
      onUploaded?.(created);
    } catch (e: any) {
      toast.error('Replace failed', e?.data?.detail || e?.message || 'Please try again');
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async () => {
    if (!image) return;
    if (!confirm('Delete this image?')) return;
    try {
      await deleteImage({ imageId: image.id }).unwrap();
      setImage(null);
      toast.success('Image deleted', 'The image has been deleted.');
      onDeleted?.(image.id);
    } catch (e: any) {
      toast.error('Delete failed', e?.data?.detail || 'Please try again');
    }
  };

  return (
    <div className="border rounded p-3">
      {!image ? (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 flex-wrap">
          <input ref={fileInput} type="file" accept={ACCEPT} onChange={onFileChange} disabled={uploading} className="max-w-full" />
          {orderId ? (
            <Button type="button" onClick={doUpload} disabled={!file || uploading}>{uploading ? 'Uploading…' : 'Upload'}</Button>
          ) : (
            <span className="text-sm text-gray-600 break-words max-w-full">
              {file ? 'Image will be uploaded when order is saved' : 'Select an image'}
            </span>
          )}
          {previewUrl && (
            <img src={previewUrl} alt="preview" className="h-16 w-16 object-cover rounded border" />
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Button type="button" variant="secondary" onClick={async () => {
              if (!image) return;
              try {
                const result: any = await dispatch(
                  apiSlice.endpoints.presignOrderImageDownload.initiate({ imageId: image.id })
                ).unwrap();
                setViewUrl(result.url as string);
                setShowLightbox(true);
              } catch (e: any) {
                toast.error('Failed to load image', e?.data?.detail || 'Please try again');
              }
            }}>View</Button>
            <Button type="button" variant="danger" onClick={onDelete}>Delete</Button>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 flex-wrap">
            <input ref={fileInput} type="file" accept={ACCEPT} onChange={onFileChange} disabled={uploading} className="max-w-full" />
            <Button type="button" onClick={doReplace} disabled={!file || uploading}>{uploading ? 'Replacing…' : 'Replace'}</Button>
            {previewUrl && (
              <img src={previewUrl} alt="preview" className="h-16 w-16 object-cover rounded border" />
            )}
          </div>
        </div>
      )}

      {showLightbox && viewUrl && image && (
        <ImageLightbox 
          url={viewUrl} 
          contentType={image.content_type} 
          onClose={() => { setShowLightbox(false); setViewUrl(null); }} 
        />
      )}
    </div>
  );
};


